import React, { useCallback, useMemo, useState } from 'react';
import { Card, Button, Spinner, Alert, Table, Form } from 'react-bootstrap';
import { userService } from '../../services/apiService';
import { useConfirm } from '../ConfirmDialog';
import { serverMessage } from '../PageState';
import { authTypeLabel, formatNumber, plural, roleLabel } from '../../utils/format';

const emptyRow = () => ({
  firstname: '', lastname: '', email: '', jobTitle: '', teamName: '', defaultOfficeName: '', enableOutlookSync: false, role: '', authType: '',
});
const blankRows = (count = 10) => Array.from({ length: count }, emptyRow);

// The grid's columns in paste order, after the row number
const COLUMNS = [
  { field: 'firstname', label: 'First name' },
  { field: 'lastname', label: 'Last name' },
  { field: 'email', label: 'Email', type: 'email' },
  { field: 'teamName', label: 'Team' },
  { field: 'defaultOfficeName', label: 'Default office' },
  { field: 'enableOutlookSync', label: 'Calendar sync', checkbox: true },
  { field: 'role', label: 'Role', placeholder: 'Basic', list: 'bulk-import-roles' },
  { field: 'authType', label: 'Sign-in method', placeholder: 'Microsoft', list: 'bulk-import-sign-in' },
  { field: 'jobTitle', label: 'Job title' },
];
const FIELDS = COLUMNS.map((column) => column.field);
const TEXT_FIELDS = COLUMNS.filter((column) => !column.checkbox).map((column) => column.field);

const ROLE_CODES = ['BASIC', 'COMPANY_ADMIN', 'DEPARTMENT_ADMIN', 'OFFICE_ADMIN', 'TEAM_ADMIN'];
// Everything the import accepts for a sign-in method, including the names the API understands
const SIGN_IN_CODES = {
  MS: 'MS', MICROSOFT: 'MS', AZURE: 'MS', OFFICE365: 'MS', 'OFFICE 365': 'MS',
  GG: 'GG', GOOGLE: 'GG',
  TT: 'TT', TEAMTODAY: 'TT', 'TEAM TODAY': 'TT', 'EMAIL AND PASSWORD': 'TT', EMAIL: 'TT', PASSWORD: 'TT',
};
const TRUE_VALUES = ['y', 'yes', 'true', '1'];
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A role as the API's code: '' when blank (the API uses Basic), null when not a role. */
const roleCode = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  const code = text.toUpperCase().replace(/[\s-]+/g, '_');
  return ROLE_CODES.includes(code) ? code : null;
};

/** A sign-in method as the API's code: '' when blank (the API uses Microsoft), null when unknown. */
const signInCode = (value) => {
  const text = String(value || '').trim().toUpperCase().replace(/\s+/g, ' ');
  if (!text) return '';
  return SIGN_IN_CODES[text] || null;
};

const rowInUse = (row) => TEXT_FIELDS.some((field) => String(row[field] || '').trim() !== '');

/** Problems in the grid, keyed "rowIndex:field", so each bad cell can be highlighted. */
const validateRows = (rows) => {
  const problems = new Map();
  const emails = new Map();
  rows.forEach((row, i) => {
    if (!rowInUse(row)) return;
    const email = row.email.trim().toLowerCase();
    if (!email) {
      problems.set(`${i}:email`, 'Email is required.');
    } else if (!EMAIL.test(email)) {
      problems.set(`${i}:email`, 'Not a valid email address.');
    } else if (emails.has(email)) {
      const first = emails.get(email);
      problems.set(`${i}:email`, `Same email as row ${first + 1}.`);
      problems.set(`${first}:email`, `Same email as row ${i + 1}.`);
    } else {
      emails.set(email, i);
    }
    if (roleCode(row.role) === null) {
      problems.set(`${i}:role`, 'Use Basic, Company admin, Department admin, Office admin or Team admin.');
    }
    if (signInCode(row.authType) === null) {
      problems.set(`${i}:authType`, 'Use Microsoft, Google or Email and password.');
    }
  });
  return problems;
};

const cellStyle = { border: 'none', borderRadius: 0 };
const invalidCellStyle = {
  ...cellStyle,
  backgroundColor: 'var(--color-danger-subtle)',
  boxShadow: 'inset 0 0 0 1px var(--color-danger-emphasis)',
};

/** The Bulk import section: paste a spreadsheet of users, check it, and create them. */
const CompanyBulkImport = ({ companyId, api, onImported }) => {
  const { confirm, confirmDialog } = useConfirm();
  const [rows, setRows] = useState(() => blankRows());
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);

  const problems = useMemo(() => validateRows(rows), [rows]);
  const rowsInUse = rows.filter(rowInUse).length;
  const problemRows = new Set([...problems.keys()].map((key) => Number(key.split(':')[0]))).size;

  const handleCellChange = (rowIndex, field, value) => {
    setRows((prev) => {
      const updated = [...prev];
      updated[rowIndex] = { ...updated[rowIndex], [field]: value };
      return updated;
    });
  };

  const handlePaste = useCallback((e) => {
    const clipboardData = e.clipboardData.getData('text');
    if (!clipboardData.includes('\t') && !clipboardData.includes('\n')) return;

    e.preventDefault();

    // Start from the focused cell; the first column is the row number
    const target = e.target;
    const cellIndex = target.closest('td') ? Array.from(target.closest('tr').children).indexOf(target.closest('td')) - 1 : 0;
    const rowElement = target.closest('tr');
    const tbody = rowElement?.closest('tbody');
    const rowIndex = tbody ? Math.max(0, Array.from(tbody.children).indexOf(rowElement)) : 0;

    const pastedRows = clipboardData.replace(/\r/g, '').split('\n').filter((line) => line.trim() !== '');

    setRows((prev) => {
      const updated = [...prev];
      const startFieldIndex = Math.max(0, Math.min(cellIndex, FIELDS.length - 1));

      pastedRows.forEach((line, i) => {
        const targetRowIndex = rowIndex + i;
        if (targetRowIndex >= updated.length) updated.push(emptyRow());
        line.split('\t').forEach((val, j) => {
          const fieldIndex = startFieldIndex + j;
          if (fieldIndex >= FIELDS.length) return;
          const field = FIELDS[fieldIndex];
          const value = field === 'enableOutlookSync' ? TRUE_VALUES.includes(val.trim().toLowerCase()) : val.trim();
          updated[targetRowIndex] = { ...updated[targetRowIndex], [field]: value };
        });
      });

      // Keep a few empty rows at the end
      while (updated.length < rowIndex + pastedRows.length + 3) updated.push(emptyRow());
      return updated;
    });
  }, []);

  const handleClear = async () => {
    if (rowsInUse > 0 && !(await confirm({
      title: 'Clear all rows?',
      body: `This removes the ${plural(rowsInUse, 'row')} you have entered. It cannot be undone.`,
      confirmLabel: 'Clear rows',
      variant: 'danger',
    }))) return;
    setRows(blankRows());
    setImportResult(null);
    setImportError(null);
  };

  const handleImport = async () => {
    if (rowsInUse === 0 || problems.size > 0) return;
    const toImport = rows.filter(rowInUse).map((row) => ({
      ...row,
      email: row.email.trim(),
      role: roleCode(row.role),
      authType: signInCode(row.authType),
    }));

    try {
      setIsImporting(true);
      setImportError(null);
      setImportResult(null);
      const response = await api(() => userService.bulkImportUsers(companyId, toImport));
      setImportResult(response.data);
      setRows(blankRows());
      onImported?.();
    } catch (err) {
      setImportError(`Could not import the users: ${serverMessage(err, err.message || 'the server did not say why')}`);
      console.error(err);
    } finally {
      setIsImporting(false);
    }
  };

  const problemList = [...problems.entries()]
    .map(([key, message]) => {
      const [row, field] = key.split(':');
      return { row: Number(row), label: COLUMNS.find((column) => column.field === field)?.label, message, key };
    })
    .sort((a, b) => a.row - b.row);

  return (
    <Card className="shadow-sm">
      {confirmDialog}
      <Card.Header className="bg-light">
        <h5 className="mb-0">Bulk import users</h5>
      </Card.Header>
      <Card.Body>
        <p className="text-muted mb-2">
          Paste rows from a spreadsheet below. Columns: <strong>first name, last name, email, team, default office, calendar sync, role, sign-in method, job title</strong>.
        </p>
        <p className="text-muted small mb-3">
          Calendar sync accepts y, yes, true or 1. A blank role means Basic and a blank sign-in method means Microsoft.
          Teams and offices that do not exist yet are created.
        </p>
        <p className="text-muted small d-md-none">The grid scrolls sideways; a wider screen shows every column at once.</p>

        {importResult && (
          <>
            <Alert variant="success" dismissible onClose={() => setImportResult(null)}>
              Imported {plural(importResult.length, 'user')}.
            </Alert>
            <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
              <Table size="sm" style={{ minWidth: '1200px' }}>
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '40px' }}>#</th>
                    <th>First name</th>
                    <th>Last name</th>
                    <th>Email</th>
                    <th>Team</th>
                    <th>Default office</th>
                    <th style={{ width: '110px' }}>Calendar sync</th>
                    <th>Role</th>
                    <th>Sign-in method</th>
                    <th>Job title</th>
                  </tr>
                </thead>
                <tbody>
                  {importResult.map((user, i) => (
                    <tr key={user.id || i}>
                      <td className="text-muted">{i + 1}</td>
                      <td>{user.firstname}</td>
                      <td>{user.lastname}</td>
                      <td>{user.email}</td>
                      <td>{user.teamName}</td>
                      <td>{user.defaultOfficeName}</td>
                      <td>{user.enableOutlookSync ? 'Yes' : 'No'}</td>
                      <td>{roleLabel(user.role)}</td>
                      <td>{authTypeLabel(user.authType)}</td>
                      <td>{user.jobTitle}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}
        {importError && (
          <Alert variant="danger" dismissible onClose={() => setImportError(null)}>
            {importError}
          </Alert>
        )}

        <datalist id="bulk-import-roles">
          {ROLE_CODES.map((code) => <option key={code} value={roleLabel(code)} />)}
        </datalist>
        <datalist id="bulk-import-sign-in">
          <option value="Microsoft" />
          <option value="Google" />
          <option value="Email and password" />
        </datalist>

        <div style={{ overflowX: 'auto' }}>
          <Table size="sm" className="mb-3" style={{ minWidth: '1200px' }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: '40px' }}>#</th>
                {COLUMNS.map((column) => (
                  <th key={column.field} style={column.checkbox ? { width: '110px' } : undefined} className={column.checkbox ? 'text-center' : undefined}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody onPaste={handlePaste}>
              {rows.map((row, i) => (
                <tr key={i}>
                  <td className="text-muted text-center align-middle" style={{ fontSize: '0.8rem' }}>{i + 1}</td>
                  {COLUMNS.map((column) => {
                    const problem = problems.get(`${i}:${column.field}`);
                    if (column.checkbox) {
                      return (
                        <td key={column.field} className="text-center align-middle">
                          <Form.Check
                            type="checkbox"
                            className="d-inline-block mb-0"
                            aria-label={`${column.label}, row ${i + 1}`}
                            checked={!!row[column.field]}
                            onChange={(e) => handleCellChange(i, column.field, e.target.checked)}
                          />
                        </td>
                      );
                    }
                    return (
                      <td key={column.field} className="p-0">
                        <Form.Control
                          size="sm"
                          type={column.type || 'text'}
                          value={row[column.field]}
                          onChange={(e) => handleCellChange(i, column.field, e.target.value)}
                          placeholder={column.placeholder}
                          list={column.list}
                          aria-label={`${column.label}, row ${i + 1}`}
                          aria-invalid={problem ? true : undefined}
                          title={problem}
                          style={problem ? invalidCellStyle : cellStyle}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        {problemList.length > 0 && (
          <div className="mb-3 small" role="status">
            <div className="text-danger fw-semibold mb-1">
              Fix {plural(problemRows, 'row')} before importing:
            </div>
            <ul className="mb-0 ps-3">
              {problemList.slice(0, 8).map((problem) => (
                <li key={problem.key}>Row {problem.row + 1}, {problem.label}: {problem.message}</li>
              ))}
              {problemList.length > 8 && <li>And {formatNumber(problemList.length - 8)} more.</li>}
            </ul>
          </div>
        )}

        <div className="d-flex flex-wrap align-items-center gap-2">
          <Button variant="outline-secondary" size="sm" onClick={() => setRows((prev) => [...prev, ...blankRows()])}>
            Add more rows
          </Button>
          <Button variant="outline-danger" size="sm" onClick={handleClear}>
            Clear all
          </Button>
          <div className="ms-auto d-flex flex-wrap align-items-center gap-3">
            <span className="text-muted">{plural(rowsInUse, 'row')} with data</span>
            <Button
              variant="primary"
              onClick={handleImport}
              disabled={isImporting || rowsInUse === 0 || problems.size > 0}
            >
              {isImporting && <Spinner as="span" animation="border" size="sm" aria-hidden="true" className="me-2" />}
              {isImporting ? 'Importing...' : 'Import users'}
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default CompanyBulkImport;
